import React, {useEffect, useMemo, useState} from 'react';
import {Icon, ListItemText} from '@mui/material';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import AppBadge from '@crema/core/AppBadge';
import AppNavLink from '@crema/core/AppNavLink';
import Box from '@mui/material/Box';
import IntlMessages from '../../../../../utility/IntlMessages';
import {checkPermission} from '../../../../../utility/helper/RouteHelper';
import {useAuthUser} from '../../../../../utility/AuthHooks';
import VerticalNavItem from './VerticalNavItem';
import {useSelector} from 'react-redux';
import {alpha} from '@mui/material/styles';
import {useNavigate} from 'react-router-dom';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';

import {ReactComponent as ActiveTrip} from 'pages/SVG/Layer_1.svg';
import {ReactComponent as LiveTracking} from 'pages/SVG/mapLocater.svg';
import {ReactComponent as Roster} from 'pages/SVG/Roster.svg';
import {ReactComponent as Adhoc} from 'pages/SVG/Adhoc.svg';
import {ReactComponent as Leave} from 'pages/SVG/Leave.svg';
import {ReactComponent as RateCard} from 'pages/SVG/RateCard.svg';
import {ReactComponent as Compliance} from 'pages/SVG/Compliances.svg';
import {ReactComponent as Reports} from 'pages/SVG/Reports.svg';
import {ReactComponent as Billing} from 'pages/SVG/Billing.svg';
import {ReactComponent as IVR} from 'pages/SVG/IVR.svg';
import {ReactComponent as Invoicing} from 'pages/SVG/Invoicing.svg';
import {ReactComponent as Feedback} from 'pages/SVG/FeedBack.svg';
import {ReactComponent as Trainingvideo} from 'pages/SVG/Training Video.svg';
import {Announcement} from '@mui/icons-material';
import {ReactComponent as Employees} from 'pages/SVG/employee.svg';
import {ReactComponent as Shifts} from 'pages/SVG/Shifts.svg';
import {ReactComponent as Departments} from 'pages/SVG/Departments.svg';
import {ReactComponent as BusinessUnits} from 'pages/SVG/BussinesUnit.svg';
import {ReactComponent as Vendors} from 'pages/SVG/Vendors.svg';
import {ReactComponent as Drivers} from 'pages/SVG/Drivers.svg';
import {ReactComponent as Vehicle} from 'pages/SVG/Vehicle.svg';
import {ReactComponent as Holidays} from 'pages/SVG/Holidays.svg';
import {ReactComponent as NodalPoints} from 'pages/SVG/NodalPoints.svg';
import {ReactComponent as EmployeeCategory} from 'pages/SVG/EmployeeCategory.svg';
import {ReactComponent as Escorts} from 'pages/SVG/Escortss.svg';
import {ReactComponent as EscortAgency} from 'pages/SVG/EscortAgency.svg';
import {ReactComponent as SiteOffice} from 'pages/SVG/Siteoffice.svg';
import {ReactComponent as SpecialEmployee} from 'pages/SVG/Special-employee.svg';
import {ReactComponent as Penalty} from 'pages/SVG/Penalty.svg';
import {ReactComponent as VehicleVariant} from 'pages/SVG/VehicleVariant.svg';
import {ReactComponent as RouteMaster} from 'pages/SVG/Routes.svg';
import {ReactComponent as VRP} from 'pages/SVG/AutoGenerated.svg';
import {ReactComponent as RouteList} from 'pages/SVG/routelists.svg';
import {ReactComponent as RosterSettings} from 'pages/SVG/RosterSettings.svg';
import {ReactComponent as RoutingRule} from 'pages/SVG/RoutingRule.svg';
import {ReactComponent as SOSSettings} from 'pages/SVG/SOSSettings.svg';
import {ReactComponent as LeaveSetting} from 'pages/SVG/LeaveSettings.svg';
import {ReactComponent as PushNotification} from 'pages/SVG/PushNotification.svg';
import {ReactComponent as ComplianceSetting} from 'pages/SVG/ComplianceSetting.svg';
import {ReactComponent as DriverNotifications} from 'pages/SVG/ComplianceSetting.svg';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import {HiUserCircle} from 'react-icons/hi';
import DescriptionIcon from '@mui/icons-material/Description';
import GpsFixedOutlinedIcon from '@mui/icons-material/GpsFixedOutlined';
import PinDropIcon from '@mui/icons-material/PinDrop';
import SupportIcon from '@mui/icons-material/Support';

const iconMap = {
  ActiveTrip: ActiveTrip,
  LiveTracking: LiveTracking,
  Roster: Roster,
  Adhoc: Adhoc,
  Leave: Leave,
  RateCard: RateCard,
  Compliance: Compliance,
  Reports: Reports,
  Billing: Billing,
  IVR: IVR,
  Invoicing: Invoicing,
  Feedback: Feedback,
  Trainingvideo: Trainingvideo,
  Announcement: Announcement,
  Employees: Employees,
  Shifts: Shifts,
  Departments: Departments,
  BusinessUnits: BusinessUnits,
  Vendors: Vendors,
  Drivers: Drivers,
  Vehicle: Vehicle,
  Holidays: Holidays,
  NodalPoints: NodalPoints,
  EmployeeCategory: EmployeeCategory,
  Escorts: Escorts,
  EscortAgency: EscortAgency,
  SiteOffice: SiteOffice,
  SpecialEmployee: SpecialEmployee,
  Penalty: Penalty,
  VehicleVariant: VehicleVariant,
  RouteMaster: RouteMaster,
  VRP: VRP,
  RouteList: RouteList,
  RosterSettings: RosterSettings,
  RoutingRule: RoutingRule,
  LeaveSetting: LeaveSetting,
  SOSSettings: SOSSettings,
  PushNotification: PushNotification,
  ComplianceSetting: ComplianceSetting,
  DriverNotifications: DriverNotifications,
  YourRides: FmdGoodOutlinedIcon,
  GpsVendors: GpsFixedOutlinedIcon,
  GpsLists: PinDropIcon,
  FuelTracking: LocalGasStationIcon,
  Taxes: HiUserCircle,
  CorpBilling: DescriptionIcon,
  Support: HelpOutlineOutlinedIcon,
};

const VerticalItem = ({level, item}) => {
  const navigate = useNavigate(); // Required for navigation
  const {user} = useAuthUser();

  const hasPermission = useMemo(
    () => checkPermission(item.permittedRole, user.role),
    [item.permittedRole, user.role]
  );
  if (!hasPermission) return null;

  const {
    adhocCount: AdhocValue,
    leaveCount: LeaveValue,
    leaveCountManager: LeaveValueManager,
    adhocCountManager: AdhocValueManager,
    scheduleCountManager: ScheduleValueManager,
  } = useSelector((state) => state.settings);

  const resolvedUrl = (item.roleUrls && item.roleUrls[user?.role]) || item.url;

  const [fc, setFC] = useState();

  useEffect(() => {
    const isActive = window.location.pathname === resolvedUrl;
    const theme = user?.userList?.theme || {};
    setFC(isActive ? theme.hoverColor || 'black' : theme.fontColor || 'white');
  }, [navigate, user, resolvedUrl]);

  const renderIcon = () => {
    if (!item.iconUrl) return item.icon;
    const IconComponent = iconMap[item.iconUrl];
    return IconComponent ? (
      <IconComponent fill={fc} height="1em" width="1em" style={{fontSize: 22}} />
    ) : null;
  };

  const renderBadge = () => {
    const badgeProps = {sx: {mr: 3.5}, className: 'menu-badge'};

    switch (item.id) {
      case 'adhocTrip':
        return <Box {...badgeProps}><AppBadge count={AdhocValue} color={item.color} /></Box>;
      case 'AdhocTrips':
        return <Box {...badgeProps}><AppBadge count={AdhocValueManager} color={item.color} /></Box>;
      case 'Leave':
        return <Box {...badgeProps}><AppBadge count={LeaveValue} color={item.color} /></Box>;
      case 'LeaveManagement':
        return user?.role === 'MANAGER' && (
          <Box {...badgeProps}><AppBadge count={LeaveValueManager} color={item.color} /></Box>
        );
      default:
        return item.module === 'RosterManager' && (
          <Box {...badgeProps}><AppBadge count={ScheduleValueManager} color={item.color} /></Box>
        );
    }
  };

  return (
    <VerticalNavItem
      level={level}
      button
      component={AppNavLink}
      to={resolvedUrl}
      activeClassName="active"
      exact={item.exact}
    >
      {item.icon && (
        <Box component="span">
          <Icon
            sx={{fontSize: 18, display: 'block', mr: 4}}
            className={clsx('nav-item-icon', 'material-icons-outlined')}
            color="action"
          >
            {renderIcon()}
          </Icon>
        </Box>
      )}

      <ListItemText
        className="nav-item-content"
        primary={item.messageId}
        classes={{primary: 'nav-item-text'}}
      />

      {renderBadge()}
    </VerticalNavItem>
  );
};

VerticalItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    hoverIcon: PropTypes.oneOfType([PropTypes.node, PropTypes.string]), // RAJ NEW LINE RESTTTTTT====>>>>>>>>
    permittedRole: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    exact: PropTypes.bool,
    messageId: PropTypes.string,
    count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    url: PropTypes.string,
    color: PropTypes.string,
  }),
  level: PropTypes.number,
};

VerticalItem.defaultProps = {};

export default React.memo(VerticalItem);
